import { Component } from '@theme/component';

/** Minimum pause before the first row in a batch appears (ms). */
const LOAD_DELAY_MS = 700;

/** Delay between each row reveal within a batch (ms). */
const ROW_STAGGER_MS = 280;

/**
 * Mom & Me feed: reveals deferred grid items row-by-row on scroll.
 */
export class MomAndMeFeed extends Component {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {boolean} */
  #isLoading = false;

  connectedCallback() {
    super.connectedCallback();

    if (this.dataset.infiniteScroll !== 'true') return;

    this.#observeViewMore();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #observeViewMore() {
    const { viewMoreNext } = this.refs;
    if (!(viewMoreNext instanceof HTMLElement)) return;

    const perPage = Number(this.dataset.looksPerPage) || 12;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#showNextBatch(perPage);
          }
        }
      },
      { rootMargin: '100px' }
    );

    this.#observer.observe(viewMoreNext);
  }

  /**
   * @returns {number}
   */
  #getColumnsPerRow() {
    const grid = this.querySelector('.mom-and-me-feed__grid');
    if (!(grid instanceof HTMLElement)) return 2;

    const styles = getComputedStyle(grid);
    const columns = window.matchMedia('(min-width: 750px)').matches
      ? styles.getPropertyValue('--mom-and-me-cols-desktop').trim()
      : styles.getPropertyValue('--mom-and-me-cols-mobile').trim();

    return Math.max(1, Number(columns) || 2);
  }

  /**
   * @param {Element[]} items
   */
  #revealRow(items) {
    for (const item of items) {
      if (!(item instanceof HTMLElement)) continue;

      item.hidden = false;
      item.classList.remove('mom-and-me-feed__item--deferred');
      item.classList.add('mom-and-me-feed__item--entering');
    }

    requestAnimationFrame(() => {
      for (const item of items) {
        if (!(item instanceof HTMLElement)) continue;
        item.classList.add('mom-and-me-feed__item--entered');
      }
    });
  }

  /**
   * @param {number} perPage
   */
  async #showNextBatch(perPage) {
    if (this.#isLoading) return;

    const deferred = this.querySelectorAll('.mom-and-me-feed__item--deferred');

    if (!deferred.length) {
      this.#removePagination();
      return;
    }

    const { viewMoreNext, loadingIndicator } = this.refs;

    this.#isLoading = true;

    if (viewMoreNext instanceof HTMLElement && this.#observer) {
      this.#observer.unobserve(viewMoreNext);
    }

    if (loadingIndicator instanceof HTMLElement) {
      loadingIndicator.hidden = false;
    }

    await new Promise((resolve) => setTimeout(resolve, LOAD_DELAY_MS));

    if (loadingIndicator instanceof HTMLElement) {
      loadingIndicator.hidden = true;
    }

    const columnsPerRow = this.#getColumnsPerRow();
    const batch = [...deferred].slice(0, perPage);
    /** @type {Element[][]} */
    const rows = [];

    for (let index = 0; index < batch.length; index += columnsPerRow) {
      rows.push(batch.slice(index, index + columnsPerRow));
    }

    for (const [rowIndex, row] of rows.entries()) {
      if (rowIndex > 0) {
        await new Promise((resolve) => setTimeout(resolve, ROW_STAGGER_MS));
      }

      this.#revealRow(row);
    }

    this.#isLoading = false;

    if (this.querySelector('.mom-and-me-feed__item--deferred')) {
      if (viewMoreNext instanceof HTMLElement && this.#observer) {
        this.#observer.observe(viewMoreNext);
      }
      return;
    }

    this.#removePagination();
  }

  #removePagination() {
    this.#observer?.disconnect();
    this.#observer = null;

    const { viewMoreNext } = this.refs;
    viewMoreNext?.closest('.mom-and-me-feed__pagination')?.remove();
  }
}

if (!customElements.get('mom-and-me-feed')) {
  customElements.define('mom-and-me-feed', MomAndMeFeed);
}
